import coreConfig from "./coreConfig";
import { showSnackbar } from "./snackbar";
const { INTERNAL_SERVER_ERROR, UNAUTHORIZED } = coreConfig.HTTP_STATUS_CODES;
const requestHeaders = {
    "Content-Type": "application/json"
};

/**
 * Fetch base function for making HTTP requests with fetch API.
 * @param {Object} options - Options object for configuring the request.
 * @param {string} options.api - The API endpoint URL.
 * @param {Object} [options.body] - The request body data for POST, PUT, PATCH requests.
 * @param {string} options.method - The HTTP method (GET, POST, PUT, DELETE, etc.).
 * @param {Object} [options.headers] - Additional headers to be included in the request.
 * @param {string} [options.responseType] - The response type expected from the server (e.g., 'json', 'text', etc.).
 * @param {function} [options.navigate] - Optional navigation function to redirect the user (e.g., to login on session expiry).
 * @returns {Promise<Object>} Promise that resolves with a response-like object ({ status, data }) or rejects with an error.
 */
const axiosBase = async ({ api, body, method, headers, responseType }) => {
    try {
        const fetchHeaders = {
            ...requestHeaders,
            ...headers
        };
        const fetchConfig = {
            method: method.toUpperCase(),
            headers: fetchHeaders,
            credentials: 'include'
        };
        if (body && method !== coreConfig.apiConfig.reqMethod.GET) {
            fetchConfig.body = typeof body === 'string' ? body : JSON.stringify(body);
        }
        let url = api;
        const isLoginReq = /login$/.test(url);
        // If api does not start with http or /backend, prepend backendUrl
        if (!/^https?:\/\//.test(api) && !api.startsWith(coreConfig.backendUrl)) {
            url = coreConfig.backendUrl.replace(/\/$/, '') + (api.startsWith('/') ? api : '/' + api);
        }
        const res = await fetch(url, fetchConfig);
        let data;
        if (responseType === 'text') {
            data = await res.text();
        } else if (responseType === 'blob') {
            data = await res.blob();
        } else {
            // Default to JSON
            data = await res.json().catch(() => null);
        }
        let message = '';
        if (res.status === INTERNAL_SERVER_ERROR) {
            message = data?.info || data?.messaage || 'Internal Server Error';
        } else if (!isLoginReq && res.status === UNAUTHORIZED) {
            message = 'Session Expired';
            setTimeout(() => {
                window.location.href = '/login';
            }, 1000);
        }
        if (message) {
            showSnackbar({ message });
        }
        // Return a response-like object for compatibility
        return { status: res.status, data };
    } catch (err) {
        let message = '';
        const statusCode = err?.response?.status;
        if (statusCode === INTERNAL_SERVER_ERROR) {
            message = err?.response?.data?.info || err?.response?.data?.messaage || 'Internal Server Error';
        }
        if (message) {
            showSnackbar({ message });
        }
    }
};

export default axiosBase;